import { apiClient } from './client';
import type {
  StudyPlanDayDto,
  StudyPlanDayViewModel,
  StudyPlanDto,
  StudyPlanTaskDto,
  StudyPlanTaskViewModel,
  StudyPlanViewModel,
} from '../types/studyPlan';
import { RUNTIME_API_CONFIG } from '../config/runtimeApi';

const STUDY_PLAN_PATH = '/api/v1/student/study-plan';

const fallbackStudyPlanDto: StudyPlanDto = {
  date_label: '今日计划',
  completion_percent: 25,
  selected_date_id: '2026-06-15',
  days: [
    { id: '2026-06-11', weekday_label: '一', day_label: '11', is_today: false },
    { id: '2026-06-12', weekday_label: '二', day_label: '12', is_today: false },
    { id: '2026-06-13', weekday_label: '三', day_label: '13', is_today: false },
    { id: '2026-06-14', weekday_label: '四', day_label: '14', is_today: false },
    { id: '2026-06-15', weekday_label: '五', day_label: '15', is_today: true },
    { id: '2026-06-16', weekday_label: '六', day_label: '16', is_today: false },
    { id: '2026-06-17', weekday_label: '日', day_label: '17', is_today: false },
  ],
  tasks: [
    {
      id: 'task-photo-search',
      title: '完成数学周测错题复盘',
      subject: '数学',
      estimated_minutes: 25,
      status: 'pending',
      reward_label: '+3',
      route_key: 'photo-search',
    },
    {
      id: 'task-words',
      title: '英语单词打卡',
      subject: '英语',
      estimated_minutes: 15,
      status: 'pending',
      reward_label: '+2',
      route_key: null,
    },
    {
      id: 'task-physics',
      title: '浮力计算专项练习',
      subject: '物理',
      estimated_minutes: 20,
      status: 'active',
      reward_label: '+2',
      route_key: null,
    },
  ],
};

const statusLabels: Record<NonNullable<StudyPlanTaskDto['status']>, string> = {
  pending: '待完成',
  active: '进行中',
  completed: '已完成',
};

const safeNumber = (value: number | null): number => {
  if (value === null || Number.isNaN(value)) {
    return 0;
  }
  return value;
};

const clampPercent = (value: number | null): number =>
  Math.min(100, Math.max(0, Math.round(safeNumber(value))));

const mapStudyPlanTaskDtoToViewModel = (
  task: StudyPlanTaskDto
): StudyPlanTaskViewModel => {
  const status = task.status ?? 'pending';
  return {
    id: task.id,
    title: task.title ?? '未命名任务',
    subject: task.subject ?? '综合',
    estimatedMinutes: Math.max(0, Math.round(safeNumber(task.estimated_minutes))),
    dateId: task.date_id ?? '',
    status,
    isCompleted: status === 'completed',
    statusLabel: statusLabels[status],
    rewardLabel: task.reward_label ?? '',
    routeKey: task.route_key ?? null,
  };
};

const fallbackDays: StudyPlanDayDto[] = fallbackStudyPlanDto.days ?? [];

const mapStudyPlanDayDtoToViewModel = (
  day: StudyPlanDayDto,
  index: number
): StudyPlanDayViewModel => ({
  id: day.id || `study-plan-day-${index + 1}`,
  weekdayLabel: day.weekday_label ?? '日',
  dayLabel: day.day_label ?? String(index + 1),
  isToday: day.is_today === true,
});

export const mapStudyPlanDtoToViewModel = (dto: StudyPlanDto): StudyPlanViewModel => {
  const tasks = (dto.tasks ?? []).map(mapStudyPlanTaskDtoToViewModel);
  const completedCount = tasks.filter((task) => task.isCompleted).length;
  const totalCount = tasks.length;
  const days = (dto.days ?? fallbackDays).map(mapStudyPlanDayDtoToViewModel);
  const selectedDateId = dto.selected_date_id ?? days.find((day) => day.isToday)?.id ?? days[0]?.id ?? '';
  const computedPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return {
    dateLabel: dto.date_label ?? '学习计划',
    selectedDateId,
    days,
    completionPercent: dto.completion_percent === null ? computedPercent : clampPercent(dto.completion_percent),
    completedCount,
    totalCount,
    tasks,
  };
};

export const getStudyPlan = async (): Promise<StudyPlanViewModel> => {
  if (!RUNTIME_API_CONFIG.enabled) {
    return mapStudyPlanDtoToViewModel(fallbackStudyPlanDto);
  }

  const response = await apiClient.get<StudyPlanDto>(STUDY_PLAN_PATH);
  return mapStudyPlanDtoToViewModel(response.data);
};
